import {
  faBell, faCaretDown, faCity, faCog, faHome, faSignOutAlt, faToolbox,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Collapse, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle,
  Nav, NavItem, Navbar, NavbarToggler,
} from 'reactstrap';
import styled from 'styled-components';

import AnonymousNavigation from 'src/components/Navigation/AnonymousNavigation';
import NavigationLogo from 'src/components/Navigation/NavigationLogo';
import { APIConfig } from 'src/config';
import { AvatarItem } from 'src/enosikit/components/Avatar/index';
import { INHIBIT_PORTFOLIO_VIEWS, USER_TYPE_ADMIN, USER_TYPE_CUSTOMER } from 'src/util/constants';
import getProposalsCount from 'src/util/proposalsCount';

const RightNav = styled(Nav).attrs({
  className: 'ms-auto',
})``;

const LeftNav = styled(Nav).attrs({
  className: 'me-auto',
})``;

const CustomerNavigationViewerFragment = graphql`
  fragment CustomerNavigation_viewer on Viewer @throwOnFieldError {
    id
    viewerUser {
      email
      givenName
      familyName
      type
    }
    proposals: rules(first:10, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_PROPOSED) {
      edges {
        node {
          uuid
        }
      }
    }
  }
`;

/**
 * Renders the root navigation items (Properties and optionally Portfolios).
 * Portfolios is hidden when the `INHIBIT_PORTFOLIO_VIEWS` feature flag is enabled.
 * @returns {React.ReactElement} The root navigation items.
 */
const renderRootNavigation = () => {
  const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);
  return (
    <>
      <NavItem>
        <Link to="/properties" className="nav-link">
          <FontAwesomeIcon icon={faHome} size="1x" className="me-2" />
          <FormattedMessage id="navigation.customer_navigation.properties.link.label" defaultMessage="Properties" />
        </Link>
      </NavItem>
      {!inhibitPortfolioViews && (
      <NavItem>
        <Link to="/portfolios" className="nav-link">
          <FontAwesomeIcon icon={faCity} size="1x" className="me-2" />
          <FormattedMessage id="navigation.customer_navigation.portfolios.link.label" defaultMessage="Portfolios" />
        </Link>
      </NavItem>
      )}
    </>
  );
};

/**
 * Renders admin-only navigation items (e.g. Admin panel link).
 * Returns null for non-admin user types.
 * @param {string} type - The viewer's user type (e.g. `USER_TYPE_ADMIN`).
 * @returns {React.ReactElement|null} The admin navigation items, or null if the user
 * is not an admin.
 */
const renderAdminNavigation = (type) => {
  if (type !== USER_TYPE_ADMIN) {
    return null;
  }
  return (
    <NavItem>
      <Link to="/admin" className="nav-link">
        <FontAwesomeIcon icon={faToolbox} size="1x" className="me-2" />
        <FormattedMessage id="navigation.customer_navigation.admin.link.label" defaultMessage="Admin" />
      </Link>
    </NavItem>
  );
};

/**
 * CustomerNavigation component that displays navigation for a customer.
 * @param {object} props - Component props
 * @param {object} props.viewer - Viewer fragment reference, resolved via useFragment
 * @returns {React.ReactComponentElement} - CustomerNavigation component
 */
function CustomerNavigation({ viewer: viewerRef = null }) {
  const viewer = useFragment(CustomerNavigationViewerFragment, viewerRef);

  const [navbarOpen, setNavbarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const toggleNavbar = () => {
    setNavbarOpen((prevState) => !prevState);
  };

  const toggleProfile = () => {
    setProfileDropdownOpen((prevState) => !prevState);
  };

  if (!viewer) {
    return <AnonymousNavigation />;
  }

  const { proposals, viewerUser } = viewer;
  const {
    givenName, familyName, email, type,
  } = viewerUser || {};
  let name = [givenName, familyName].filter(Boolean).join(' ');
  if (name === '') {
    name = email;
  }

  const cobrand = APIConfig().cobrand();
  const primaryBrand = APIConfig().primaryBrand();

  return (
    <Navbar light expand="lg">
      <Container className="d-flex flex-wrap justify-content-between px-0 px-sm-3">
        <Link to="/" className="navbar-brand">
          <NavigationLogo cobrand={cobrand} primaryBrand={primaryBrand} />
        </Link>
        <NavbarToggler onClick={toggleNavbar} />
        <Collapse isOpen={navbarOpen} navbar>
          <LeftNav navbar>
            {renderRootNavigation()}
            {renderAdminNavigation(type)}
          </LeftNav>
          <RightNav navbar>
            {type === USER_TYPE_CUSTOMER && (
              <NavItem>
                <Link to="/trade-rules" className="nav-link">
                  <span className="fa-layers fa-fw" style={{ fontSize: '1.5rem', marginTop: '0.6rem' }}>
                    <FontAwesomeIcon icon={faBell} />
                    <span className="fa-layers-counter danger fw-bold" style={{ fontSize: '2.5rem', paddingTop: '.8rem' }}>{getProposalsCount(proposals)}</span>
                  </span>
                </Link>
              </NavItem>
            )}
            <Dropdown
              nav
              inNavbar
              isOpen={profileDropdownOpen}
              toggle={toggleProfile}
            >
              <DropdownToggle nav>
                <AvatarItem
                  primaryText={name}
                  secondaryText={email}
                  avatarImage={
                    <FontAwesomeIcon icon={faUserCircle} size="3x" />
                  }
                  className="d-inline-flex"
                />
                <FontAwesomeIcon
                  icon={faCaretDown}
                  size="1x"
                  className="ms-2"
                />
              </DropdownToggle>
              <DropdownMenu end>
                <Link
                  to="/profile/settings"
                  activeClassName="active"
                  className="dropdown-item"
                  role="menuitem"
                >
                  <FontAwesomeIcon icon={faCog} className="me-2" />
                  <FormattedMessage id="navigation.customer_navigation.current_user_settings.link.label" defaultMessage="Profile settings" />
                </Link>
                <DropdownItem divider />
                <Link
                  to="/logout"
                  activeClassName="active"
                  className="dropdown-item"
                  role="menuitem"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                  <FormattedMessage id="navigation.customer_navigation.log_out.link.label" defaultMessage="Log out" />
                </Link>
              </DropdownMenu>
            </Dropdown>
          </RightNav>
        </Collapse>
      </Container>
    </Navbar>
  );
}

// viewer is a Relay fragment reference (opaque shape) until useFragment resolves it.
CustomerNavigation.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default CustomerNavigation;
